<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.updateProduct") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #e1423d">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs" novalidate>
        <div class="row">
          <!-- Start:: Main Image Upload Input -->
          <base-image-upload-input
            col="12"
            identifier="main_image"
            :preSelectedImage="data.main_image.path"
            :placeholder="$t('PLACEHOLDERS.productImg')"
            @selectImage="selectMainImage"
            required
          />
          <!-- End:: Main Image Upload Input -->

          <!-- Start:: Additional Images Upload Input -->
          <div class="col-12 my-5">
            <base-multi-image-upload-input
              @onFileSelect="handleAdditionalImages"
              @onFileRemove="removeAdditionalImage"
              :urls="originalImages.map((img) => img.url)"
            >
              {{ $t("PLACEHOLDERS.additionalImages") || "Additional Images" }}
            </base-multi-image-upload-input>
          </div>
          <!-- End:: Additional Images Upload Input -->
          <!-- Start:: Name Input (Arabic) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codename')"
            v-model.trim="data.name"
            :maxlength="100"
            required
          />
          <!-- End:: Name Input (Arabic) -->

          <!-- Start:: Name Input (English) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codenameEn')"
            v-model.trim="data.nameEn"
            :maxlength="100"
            required
          />
          <!-- End:: Name Input (English) -->

          <!-- Start:: Description Input (Arabic) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProd')"
            v-model.trim="data.descProd"
            :maxlength="500"
          />
          <!-- End:: Description Input (Arabic) -->

          <!-- Start:: Description Input (English) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProdEn')"
            v-model.trim="data.descProdEn"
            :maxlength="500"
          />
          <!-- End:: Description Input (English) -->
          <!-- Start:: Category Select Input -->
          <base-select-input
            col="6"
            :optionsList="categoriesList"
            :placeholder="$t('PLACEHOLDERS.category') || 'Product Category'"
            v-model="data.category_id"
            required
          />
          <!-- End:: Category Select Input -->

          <!-- Start:: Weight or Size Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.weightOrSize') || 'Weight or Size'"
            v-model.number="data.weight_or_size"
            step="0.01"
            required
          />
          <!-- End:: Weight or Size Input -->

          <!-- Start:: Weight Unit Select Input -->
          <base-select-input
            col="6"
            :optionsList="measurementUnitOptions"
            :placeholder="$t('PLACEHOLDERS.weightUnit') || 'Weight Unit'"
            v-model="data.weight_unit"
            required
          />
          <!-- End:: Weight Unit Select Input -->
          <base-select-input
            col="6"
            :optionsList="frozenStatusList"
            :placeholder="$t('PLACEHOLDERS.frozen_status') || 'Freeze Status'"
            v-model="data.frozen_status"
            required
            hide-details
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.productCode') || 'Product Code'"
            v-model.trim="data.product_code"
          />

          <!-- Start:: Packaging & Inventory (General) -->
          <base-select-input
            col="6"
            :optionsList="packagingUnitOptions"
            :placeholder="$t('PLACEHOLDERS.packagingUnit') || 'Packaging Unit'"
            v-model="data.packaging_unit"
            required
          />

          <base-select-input
            col="6"
            :optionsList="itemUnitOptions"
            :placeholder="$t('PLACEHOLDERS.itemUnit') || 'Item Unit'"
            v-model="data.item_unit"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.availableUnits') || 'Available Units'"
            v-model.number="data.available_units"
            step="0.01"
            min="0"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.itemsPerUnit') || 'Items Per Unit'"
            v-model.number="data.items_per_unit"
            step="1"
            min="1"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.lowStockThreshold') || 'Low Stock Threshold (Optional)'"
            v-model.number="data.low_stock_threshold"
            step="1"
            min="0"
          />
          <!-- End:: Packaging & Inventory (General) -->

          <!-- Start:: Prices Section -->
          <div class="col-12">
            <div class="prices-section-header mb-4">
              <h5 class="mb-0" style="color: #e1423d; font-weight: 600">
                <i class="fas fa-dollar-sign mr-2"></i>
                {{
                  $t("PLACEHOLDERS.pricesByQuantity") || "Prices by Quantity"
                }}
              </h5>
            </div>

            <div class="prices-list">
              <div
                v-for="(price, index) in data.prices"
                :key="index"
                class="price-card mb-4"
              >
                <div class="price-card-header">
                  <div class="price-card-title">
                    <i class="fas fa-tag mx-2" style="color: #e1423d"></i>
                    <span style="font-weight: 600"
                      >{{ $t("PLACEHOLDERS.priceTier") || "Price Tier" }} #{{
                        index + 1
                      }}</span
                    >
                  </div>
                  <v-btn
                    v-if="data.prices.length > 1 && index !== 0"
                    @click="removePrice(index)"
                    color="error"
                    icon
                    small
                    class="remove-price-btn"
                  >
                    <i class="fas fa-trash"></i>
                  </v-btn>
                </div>

                <div class="price-card-body">
                  <div class="row">
                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="$t('PLACEHOLDERS.quantity') || 'Quantity'"
                        v-model.number="price.quantity"
                        step="0.01"
                        required
                        :disabled="index === 0"
                      />
                    </div>

                    <!-- Price and Minimum only; packaging/inventory are product-level -->
                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="$t('PLACEHOLDERS.riyal_price') || 'Price'"
                        v-model.number="price.price"
                        step="0.01"
                        required
                      />
                    </div>

                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="
                          $t('PLACEHOLDERS.minimumQuantity') ||
                          'Minimum Quantity'
                        "
                        v-model.number="price.minimum_quantity"
                        step="0.01"
                        min="1"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <v-btn @click="addPrice" outlined class="add-price-btn">
              <i class="fas fa-plus mr-2"></i>
              {{ $t("BUTTONS.addPrice") || "Add Price" }}
            </v-btn>
          </div>
          <!-- End:: Prices Section -->

          <!-- Start:: Product Status Switch Input -->
          <div class="input_wrapper switch_wrapper my-5 col-6">
            <label
              class="form-label"
              style="margin: 0 13px !important; color: #e1423d"
            >
              {{ $t("PLACEHOLDERS.productStatus") || "Product Status" }}:
              <span class="text-danger">*</span>
            </label>
            <v-switch
              color="green"
              :label="
                data.is_active
                  ? $t('PLACEHOLDERS.active')
                  : $t('PLACEHOLDERS.notActive')
              "
              v-model="data.is_active"
              hide-details
            ></v-switch>
          </div>
          <!-- End:: Product Status Switch Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BaseImageUploadInput from "../../../components/formInputs/BaseImageUploadInput.vue";
import BaseMultiImageUploadInput from "../../../components/formInputs/BaseMultiImageUploadInput.vue";

export default {
  name: "EditProduct",

  components: {
    BaseImageUploadInput,
    BaseMultiImageUploadInput,
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        nameEn: null,
        descProd: null,
        descProdEn: null,
        main_image: {
          path: null,
          file: null,
        },
        category_id: null,
        frozen_status: null,
        product_code: null,
        weight_or_size: null,
        weight_unit: null,
        // Packaging & Inventory (product-level)
        packaging_unit: null,
        item_unit: null,
        available_units: null,
        items_per_unit: 1,
        low_stock_threshold: null,
        prices: [],
        is_active: true,
      },
      // End:: Data Collection To Send

      categoriesList: [],
      additionalImages: [],
      originalPrices: [], // Store original prices with IDs for comparison
      originalImages: [], // Store current images with IDs (gets modified when images are removed)
      deletedImageIds: [], // Track IDs of images that were deleted
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    measurementUnitOptions() {
      const isArabic = this.getAppLocale === "ar";
      return [
        {
          id: 1,
          name_ar: "كيس",
          name_en: "Bag",
          name: isArabic ? "كيس" : "Bag",
          value: "Bag",
        },
        {
          id: 2,
          name_ar: "بلوك",
          name_en: "Block",
          name: isArabic ? "بلوك" : "Block",
          value: "Block",
        },
        {
          id: 3,
          name_ar: "كرتون",
          name_en: "Box",
          name: isArabic ? "كرتون" : "Box",
          value: "Box",
        },
        {
          id: 4,
          name_ar: "جالون",
          name_en: "Gallon",
          name: isArabic ? "جالون" : "Gallon",
          value: "Gallon",
        },
        {
          id: 5,
          name_ar: "كيلو",
          name_en: "kg",
          name: isArabic ? "كيلو" : "kg",
          value: "kg",
        },
        {
          id: 6,
          name_ar: "لتر",
          name_en: "Ltr",
          name: isArabic ? "لتر" : "Ltr",
          value: "Ltr",
        },
        {
          id: 7,
          name_ar: "سطل",
          name_en: "Pail",
          name: isArabic ? "سطل" : "Pail",
          value: "Pail",
        },
        {
          id: 8,
          name_ar: "حبة",
          name_en: "Pc",
          name: isArabic ? "حبة" : "Pc",
          value: "Pc",
        },
        {
          id: 9,
          name_ar: "باكيت",
          name_en: "PKT",
          name: isArabic ? "باكيت" : "PKT",
          value: "PKT",
        },
        {
          id: 10,
          name_ar: "تنك",
          name_en: "Tin",
          name: isArabic ? "تنك" : "Tin",
          value: "Tin",
        },
        {
          id: 11,
          name_ar: "ربطة",
          name_en: "punch",
          name: isArabic ? "ربطة" : "punch",
          value: "punch",
        },
        {
          id: 12,
          name_ar: "طبق",
          name_en: "Tray",
          name: isArabic ? "طبق" : "Tray",
          value: "Tray",
        },
        {
          id: 13,
          name_ar: "صحن",
          name_en: "Plate",
          name: isArabic ? "صحن" : "Plate",
          value: "Plate",
        },
      ];
    },
    packagingUnitOptions() {
      const isArabic = this.getAppLocale === "ar";
      return [
        { id: 1, value: "pack_bag", name: isArabic ? "كيس تعبئة" : "Pack Bag" },
        { id: 2, value: "carton", name: isArabic ? "كرتونة" : "Carton" },
        { id: 3, value: "gallon", name: isArabic ? "جالون" : "Gallon" },
        { id: 4, value: "bucket", name: isArabic ? "سطل" : "Bucket" },
        { id: 5, value: "tank", name: isArabic ? "تنك" : "Tank" },
        { id: 6, value: "packet", name: isArabic ? "باكيت" : "Packet" },
        { id: 7, value: "bundle", name: isArabic ? "ربطة" : "Bundle" },
        { id: 8, value: "tray", name: isArabic ? "طبق" : "Tray" },
      ];
    },
    itemUnitOptions() {
      const isArabic = this.getAppLocale === "ar";
      return [
        { id: 1, value: "bag", name: isArabic ? "كيس" : "Bag" },
        { id: 2, value: "kg", name: isArabic ? "كيلوجرام" : "Kilogram" },
        { id: 3, value: "liter", name: isArabic ? "لتر" : "Liter" },
        { id: 4, value: "piece", name: isArabic ? "قطعة" : "Piece" },
        { id: 5, value: "plate", name: isArabic ? "صحن" : "Plate" },
        { id: 6, value: "drzn", name: isArabic ? "دزينة" : "Dozen" },
      ];
    },
    frozenStatusList() {
      return [
        { id: true, name: this.$t("PLACEHOLDERS.frozen"), value: "frozen" },
        { id: false, name: this.$t("PLACEHOLDERS.chilled"), value: "chilled" },
      ];
    },
  },

  methods: {
    getMeasurementUnitOption(unit) {
      const matched = this.measurementUnitOptions.find(
        (item) =>
          item.value === unit ||
          item.name === unit ||
          item.name_ar === unit ||
          item.name_en === unit
      );
      return matched || (unit ? { id: unit, name: unit, value: unit } : null);
    },
    getMeasurementUnitName(unit) {
      return unit && typeof unit === "object" ? unit.name : unit;
    },

    getPackagingUnitName(unit) {
      return unit && typeof unit === "object" ? unit.value || unit.name : unit;
    },

    getItemUnitName(unit) {
      return unit && typeof unit === "object" ? unit.value || unit.name : unit;
    },

    getPackagingUnitOption(unit) {
      const normalizedUnit = unit?.value || unit;
      return (
        this.packagingUnitOptions.find(
          (item) =>
            item.value === normalizedUnit || item.name === normalizedUnit
        ) ||
        (normalizedUnit
          ? { id: normalizedUnit, value: normalizedUnit, name: normalizedUnit }
          : null)
      );
    },

    getItemUnitOption(unit) {
      const normalizedUnit = unit?.value || unit;
      return (
        this.itemUnitOptions.find(
          (item) =>
            item.value === normalizedUnit || item.name === normalizedUnit
        ) ||
        (normalizedUnit
          ? { id: normalizedUnit, value: normalizedUnit, name: normalizedUnit }
          : null)
      );
    },

    // Start:: Image Handlers
    selectMainImage(selectedImage) {
      if (selectedImage.file) {
        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (selectedImage.file.size > maxSize) {
          this.$message.error(
            this.$t("VALIDATION.fileSizeExceeded") ||
              "File size must not exceed 10MB"
          );
          return;
        }
        // Validate file type (images only)
        if (!selectedImage.file.type.startsWith("image/")) {
          this.$message.error(
            this.$t("VALIDATION.selectedFileMustBeImage") ||
              "Only image files are allowed"
          );
          return;
        }
      }
      this.data.main_image = selectedImage;
    },

    handleAdditionalImages(files) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const validFiles = [];

      files.forEach((file) => {
        if (file.size > maxSize) {
          this.$message.error(
            `${file.name}: ${
              this.$t("VALIDATION.fileSizeExceeded") ||
              "File size must not exceed 10MB"
            }`
          );
          return;
        }
        if (!file.type.startsWith("image/")) {
          this.$message.error(
            `${file.name}: ${
              this.$t("VALIDATION.selectedFileMustBeImage") ||
              "Only image files are allowed"
            }`
          );
          return;
        }
        validFiles.push(file);
      });

      this.additionalImages = validFiles;
    },

    removeAdditionalImage(index) {
      // The index corresponds to the position in the originalImages array
      // (new images are handled internally by the component)

      // It's an old image - remove from originalImages
      const oldImg = this.originalImages[index];

      // Only add to deletedImageIds if the image has a valid ID
      if (oldImg && oldImg.id) {
        this.deletedImageIds.push(oldImg.id);
      }

      // Remove from originalImages array
      this.originalImages.splice(index, 1);
    },
    // End:: Image Handlers

    // Start:: Price Management
    addPrice() {
      this.data.prices.push({
        quantity: null,
        price: null,
        minimum_quantity: 1,
      });
    },

    removePrice(index) {
      if (this.data.prices.length > 1) {
        this.data.prices.splice(index, 1);
      }
    },
    // End:: Price Management

    // Start:: Get Categories
    async getCategories() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "product-categories?paginate=false&filter[is_active]=true",
          params: {
            limit: 0,
            page: 0,
            isActive: 1,
          },
        });
        this.categoriesList = (res.data.data?.data || res.data.data || []).map(
          (category) => ({
            id: category.id,
            name: category.name,
          })
        );
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Categories

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const productId = this.$route.params.id;

      try {
        // Step 1: Update main product data (excluding prices and additional images)
        const REQUEST_DATA = new FormData();
        // Start:: Append Request Data
        if (this.data.name) {
          REQUEST_DATA.append("name[ar]", this.data.name);
        }
        if (this.data.nameEn) {
          REQUEST_DATA.append("name[en]", this.data.nameEn);
        }
        if (this.data.descProd) {
          REQUEST_DATA.append("description[ar]", this.data.descProd);
        }
        if (this.data.descProdEn) {
          REQUEST_DATA.append("description[en]", this.data.descProdEn);
        }
        if (this.data.main_image.file) {
          REQUEST_DATA.append("main_image", this.data.main_image.file);
        }
        if (this.data.category_id) {
          REQUEST_DATA.append("category_id", this.data.category_id?.id);
        }
        REQUEST_DATA.append(
          "frozen_status",
          this.data.frozen_status?.value || ""
        );
        if (this.data.product_code) {
          REQUEST_DATA.append("product_code", this.data.product_code);
        }
        if (this.data.weight_or_size) {
          REQUEST_DATA.append("weight_or_size", this.data.weight_or_size);
        }
        if (this.data.weight_unit) {
          REQUEST_DATA.append(
            "weight_unit",
            this.getMeasurementUnitName(this.data.weight_unit)
          );
        }

        // Append packaging & inventory (product-level)
        if (this.data.packaging_unit) {
          REQUEST_DATA.append(
            "packaging_unit",
            this.getPackagingUnitName(this.data.packaging_unit)
          );
        }
        if (this.data.item_unit) {
          REQUEST_DATA.append(
            "item_unit",
            this.getItemUnitName(this.data.item_unit)
          );
        }
        if (
          this.data.available_units !== undefined &&
          this.data.available_units !== null
        ) {
          REQUEST_DATA.append("available_units", this.data.available_units);
        }
        if (
          this.data.items_per_unit !== undefined &&
          this.data.items_per_unit !== null
        ) {
          REQUEST_DATA.append("items_per_unit", this.data.items_per_unit);
        }
        if (
          this.data.low_stock_threshold !== undefined &&
          this.data.low_stock_threshold !== null
        ) {
          REQUEST_DATA.append(
            "low_stock_threshold",
            this.data.low_stock_threshold
          );
        }

        REQUEST_DATA.append("is_active", this.data.is_active ? 1 : 0);
        // End:: Append Request Data

        await this.$axios({
          method: "PATCH",
          url: `products/${productId}`,
          data: REQUEST_DATA,
        });

        // Step 2: Handle prices - Add new prices
        const newPrices = this.data.prices.filter((price) => !price.id);
        for (const price of newPrices) {
          await this.$axios({
            method: "POST",
            url: `products/${productId}/prices`,
            data: {
              quantity: price.quantity,
              price: price.price,
              minimum_quantity: price.minimum_quantity,
            },
          });
        }

        // Step 3: Handle prices - Update existing prices
        const existingPrices = this.data.prices.filter((price) => price.id);
        for (const price of existingPrices) {
          const originalPrice = this.originalPrices.find(
            (p) => p.id === price.id
          );
          if (originalPrice) {
            // Check if price has changed
            const hasChanged =
              originalPrice.quantity !== price.quantity ||
              originalPrice.price !== price.price ||
              originalPrice.minimum_quantity !== price.minimum_quantity;

            if (hasChanged) {
              await this.$axios({
                method: "PATCH",
                url: `products/${productId}/prices/${price.id}`,
                data: {
                  quantity: price.quantity,
                  price: price.price,
                  minimum_quantity: price.minimum_quantity,
                },
              });
            }
          }
        }

        // Step 4: Handle prices - Delete removed prices
        const currentPriceIds = this.data.prices
          .filter((p) => p.id)
          .map((p) => p.id);
        const removedPrices = this.originalPrices.filter(
          (p) => !currentPriceIds.includes(p.id)
        );
        for (const removedPrice of removedPrices) {
          await this.$axios({
            method: "DELETE",
            url: `products/${productId}/prices/${removedPrice.id}`,
          });
        }

        // Step 5: Handle images - Add new images
        for (const imageFile of this.additionalImages) {
          const imageFormData = new FormData();
          imageFormData.append("image", imageFile);
          await this.$axios({
            method: "POST",
            url: `products/${productId}/images`,
            data: imageFormData,
          });
        }
        // Step 6: Handle images - Delete removed images
        for (const imageId of this.deletedImageIds) {
          // Only delete if imageId is valid (not null or undefined)
          if (imageId) {
            await this.$axios({
              method: "DELETE",
              url: `products/${productId}/images/${imageId}`,
            });
          }
        }

        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        this.$router.push({ path: "/products/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || error.message || "An error occurred"
        );
      }
    },
    // End:: Submit Form

    // Start:: Get Product Images
    async getProductImages() {
      try {
        const productId = this.$route.params.id;
        let res = await this.$axios({
          method: "GET",
          url: `products/${productId}/images`,
        });

        if (
          res.data.data &&
          Array.isArray(res.data.data) &&
          res.data.data.length > 0
        ) {
          this.originalImages = res.data.data.map((img) => ({
            id: img.id || null,
            url: img.url || null,
          }));
        } else {
          this.originalImages = [];
        }
      } catch (error) {
        console.log(error.response?.data?.message);
        // If endpoint fails, set empty array
        this.originalImages = [];
      }
    },
    // End:: Get Product Images

    // Start:: Show Product Data
    async showProduct() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `products/${this.$route.params.id}?include=translations,category,prices`,
        });
        const product = res.data.data;

        // Set translations
        if (product.translations) {
          this.data.name = product.translations.ar?.name || product.name;
          this.data.nameEn = product.translations.en?.name || product.name;
          this.data.descProd =
            product.translations.ar?.description || product.description;
          this.data.descProdEn =
            product.translations.en?.description || product.description;
        } else {
          this.data.name = product.name_ar || product.name;
          this.data.nameEn = product.name_en || product.name;
          this.data.descProd = product.description_ar || product.description;
          this.data.descProdEn = product.description_en || product.description;
        }

        // Set main image
        if (product.main_image_url) {
          this.data.main_image.path = product.main_image_url;
        }

        // Fetch additional images from dedicated endpoint
        await this.getProductImages();
        // Reset deleted image IDs when loading product
        this.deletedImageIds = [];

        // Set category
        if (product.category_id) {
          this.data.category_id = {
            id: product.category_id,
            name:
              product.category.name ||
              product.category.translations?.ar?.name ||
              product.category.translations?.en?.name,
          };
        }

        // Set freeze status
        this.data.frozen_status = this.frozenStatusList.find(
          (item) => item.value === product.frozen_status
        );
        this.data.product_code =
          product.product_code || product.prices?.[0]?.product_code || null;

        // Set weight and unit
        this.data.weight_or_size = product.weight_or_size || null;
        this.data.weight_unit = this.getMeasurementUnitOption(
          product.weight_unit
        );

        // Set prices
        if (product.prices && product.prices.length > 0) {
          // Product-level packaging and inventory (source of truth)
          this.data.packaging_unit = this.getPackagingUnitOption(
            product.packaging_unit ||
              (product.prices[0] &&
                (product.prices[0].packaging_unit ||
                  product.prices[0].quantity_unit))
          );
          this.data.item_unit = this.getItemUnitOption(
            product.item_unit ||
              (product.prices[0] &&
                (product.prices[0].item_unit ||
                  product.prices[0].quantity_unit))
          );
          this.data.available_units =
            product.available_units ??
            product.prices[0]?.available_units ??
            product.prices[0]?.available_quantity ??
            null;
          this.data.items_per_unit =
            product.items_per_unit ?? product.prices[0]?.items_per_unit ?? 1;
          this.data.low_stock_threshold =
            product.low_stock_threshold ??
            product.prices[0]?.low_stock_threshold ??
            null;

          this.originalPrices = product.prices.map((price) => ({
            id: price.id,
            quantity: price.quantity,
            price: parseFloat(price.price),
            minimum_quantity: price.minimum_quantity,
          }));

          this.data.prices = product.prices.map((price) => ({
            id: price.id,
            quantity: price.quantity,
            price: parseFloat(price.price),
            minimum_quantity: price.minimum_quantity,
          }));
        } else {
          // Default price if none exists
          this.originalPrices = [];
          this.data.prices = [
            {
              quantity: null,
              price: null,
              minimum_quantity: 1,
            },
          ];
        }

        // Set active status
        this.data.is_active =
          product.is_active !== undefined ? product.is_active : true;
      } catch (error) {
        this.isWaitingRequest = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || "Failed to load product data"
        );
      }
    },
    // End:: Show Product Data
  },

  created() {
    // Start:: Fire Methods
    this.getCategories();
    this.showProduct();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.prices-section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1423d;
}

.price-card {
  /* background: #ffffff; */
  border: 1px solid #e1423d;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: hidden;
}

.price-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #e1423d;
}

.price-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  /* background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); */
  border-bottom: 1px solid #e1423d;
}

.price-card-title {
  display: flex;
  align-items: center;
  /* color: #333; */
  font-size: 1rem;
}

.remove-price-btn {
  border: 1px solid #e1423d;
  margin: 0;
}

.price-card-body {
  padding: 1.25rem;
}

.price-field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
}

.add-price-btn {
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem;
  font-weight: 500;
  border: 2px dashed #e1423d;
  transition: all 0.3s ease;
  color: #e1423d;
}

.add-price-btn:hover {
  background-color: #e1423d;
  color: white;
  border-style: solid;
}

@media (max-width: 768px) {
  .price-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .remove-price-btn {
    align-self: flex-end;
  }
}
</style>
